import { Injectable } from '@angular/core'
import { AuthSsoUser, TokenStorage } from '@shared/models/auth/auth.models'

/**
 * Token Storage Service
 *
 * Handles secure storage and retrieval of authentication tokens.
 * Provides both session and persistent storage options with encryption support.
 *
 * Features:
 * - Secure token storage with encryption
 * - Session and persistent storage options
 * - Automatic cleanup of expired tokens
 * - Storage quota management
 * - Error handling and fallback mechanisms
 */
@Injectable({ providedIn: 'root' })
export class TokenStorageService {
  private readonly TOKEN_KEY = 'gbc_auth_token'
  private readonly REFRESH_TOKEN_KEY = 'gbc_refresh_token'
  private readonly USER_DATA_KEY = 'gbc_user_data'
  private readonly STORAGE_PREFIX = 'gbc_'
  /** Separate key for SSO token — never shared with regular TOKEN_KEY */
  private readonly SSO_TOKEN_KEY = 'gbc_sso_token'
  private readonly SSO_USER_INFO = 'gbc_sso_user'
  private readonly SSO_REFRESH_TOKEN_KEY = 'gbc_sso_refresh_token'
  private readonly SSO_REFRESH_EXPIRES_KEY = 'gbc_sso_refresh_expires'

  /**
   * Stores authentication tokens and user data
   * @param tokenStorage Token storage data
   * @param persistent Whether to use persistent storage (localStorage) or session storage
   */
  setTokens(tokenStorage: TokenStorage, persistent: boolean = true): void {
    try {
      const storage = persistent ? localStorage : sessionStorage
      const encryptedData = this.encryptData(tokenStorage)

      storage.setItem(this.TOKEN_KEY, encryptedData.token)
      storage.setItem(this.REFRESH_TOKEN_KEY, encryptedData.refreshToken)
      storage.setItem(this.USER_DATA_KEY, encryptedData.userData)

      // Set expiration timestamp for automatic cleanup
      const expirationKey = `${this.STORAGE_PREFIX}exp_${tokenStorage.userId}`
      storage.setItem(expirationKey, tokenStorage.refreshTokenExpiresAt)
    } catch (error) {
      console.error('Error storing tokens:', error)
      // Fallback to session storage if localStorage fails
      if (persistent) {
        this.setTokens(tokenStorage, false)
      }
    }
  }
  setSSoTokens(token: string, refresh: string, refreshExpires: string, ssoUserInfo: string): void {
    try {
      localStorage.setItem(this.SSO_TOKEN_KEY, token)
      localStorage.setItem(this.SSO_REFRESH_TOKEN_KEY, refresh)
      localStorage.setItem(this.SSO_REFRESH_EXPIRES_KEY, refreshExpires)
      localStorage.setItem(this.SSO_USER_INFO, ssoUserInfo)
    } catch (error) {
      console.error('Error storing SSO tokens:', error)
    }
  }

  /** Clears SSO-specific tokens from storage */
  clearSsoTokens(): void {
    try {
      localStorage.removeItem(this.SSO_TOKEN_KEY)
      localStorage.removeItem(this.SSO_REFRESH_TOKEN_KEY)
      localStorage.removeItem(this.SSO_REFRESH_EXPIRES_KEY)
      localStorage.removeItem(this.SSO_USER_INFO)
    } catch (error) {
      console.error('Error clearing SSO tokens:', error)
    }
  }

  /**
   * Retrieves stored authentication tokens
   * @param persistent Whether to check persistent storage first
   * @returns Token storage data or null if not found/expired
   */
  getTokens(persistent: boolean = true): TokenStorage | null {
    try {
      const storage = persistent ? localStorage : sessionStorage

      const token = storage.getItem(this.TOKEN_KEY)
      const refreshToken = storage.getItem(this.REFRESH_TOKEN_KEY)
      const userData = storage.getItem(this.USER_DATA_KEY)

      if (!token || !refreshToken || !userData) {
        return null
      }

      const decryptedData = this.decryptData({
        token,
        refreshToken,
        userData,
      })

      // Check if refresh token is expired
      const expirationDate = new Date(decryptedData.refreshTokenExpiresAt)
      if (Date.now() >= expirationDate.getTime()) {
        this.clearTokens(persistent)
        return null
      }

      return decryptedData
    } catch (error) {
      console.error('Error retrieving tokens:', error)
      return null
    }
  }

  /**
   * Updates only the access token
   * @param token New access token
   * @param persistent Whether to update persistent storage
   */
  updateAccessToken(token: string, persistent: boolean = true): void {
    try {
      const storage = persistent ? localStorage : sessionStorage
      const encryptedToken = this.encrypt(token)
      storage.setItem(this.TOKEN_KEY, encryptedToken)
    } catch (error) {
      console.error('Error updating access token:', error)
    }
  }

  /**
   * Clears all stored authentication data
   * @param persistent Whether to clear persistent storage
   */
  clearTokens(persistent: boolean = true): void {
    try {
      const storage = persistent ? localStorage : sessionStorage

      // Clear main token data
      storage.removeItem(this.TOKEN_KEY)
      storage.removeItem(this.REFRESH_TOKEN_KEY)
      storage.removeItem(this.USER_DATA_KEY)

      // Always clear SSO tokens from localStorage on logout
      if (persistent) {
        this.clearSsoTokens()
      }

      // Clear any expiration timestamps
      this.clearExpirationTimestamps(storage)
    } catch (error) {
      console.error('Error clearing tokens:', error)
    }
  }

  /**
   * Checks if tokens are stored and valid
   * @param persistent Whether to check persistent storage
   * @returns Whether valid tokens are stored
   */
  hasValidTokens(persistent: boolean = true): boolean {
    const tokens = this.getTokens(persistent)
    return tokens !== null
  }

  /**
   * Gets the stored access token
   * @param persistent Whether to check persistent storage
   * @returns Access token or null
   */
  getAccessToken(persistent: boolean = true): string | null {
    try {
      const storage = persistent ? localStorage : sessionStorage
      const encryptedToken = storage.getItem(this.TOKEN_KEY)

      if (!encryptedToken) {
        return null
      }

      return this.decrypt(encryptedToken)
    } catch (error) {
      console.error('Error retrieving access token:', error)
      return null
    }
  }

  /**
   * Gets the stored refresh token
   * @param persistent Whether to check persistent storage
   * @returns Refresh token or null
   */
  getRefreshToken(persistent: boolean = true): string | null {
    try {
      const storage = persistent ? localStorage : sessionStorage
      const encryptedToken = storage.getItem(this.REFRESH_TOKEN_KEY)

      if (!encryptedToken) {
        return null
      }

      return this.decrypt(encryptedToken)
    } catch (error) {
      console.error('Error retrieving refresh token:', error)
      return null
    }
  }

  /**
   * Cleans up expired tokens from storage
   */
  cleanupExpiredTokens(): void {
    try {
      const storages = [localStorage, sessionStorage]
      storages.forEach(storage => {
        const keys = Object.keys(storage)
        const expirationKeys = keys.filter(key => key.startsWith(`${this.STORAGE_PREFIX}exp_`))

        expirationKeys.forEach(key => {
          const expirationDate = storage.getItem(key)
          if (expirationDate && Date.now() >= new Date(expirationDate).getTime()) {
            // Extract user ID from key and clear related data
            const userId = key.replace(`${this.STORAGE_PREFIX}exp_`, '')
            this.clearUserTokens(userId, storage)
          }
        })
      })
    } catch (error) {
      console.error('Error cleaning up expired tokens:', error)
    }
  }

  /**
   * Gets storage usage information
   * @returns Storage usage statistics
   */
  getStorageInfo(): { used: number; available: number; percentage: number } {
    try {
      const storage = localStorage
      let used = 0

      Object.keys(storage).forEach(key => {
        used += (storage.getItem(key)?.length || 0) + key.length
      })

      // Estimate available space (most browsers have ~5-10MB limit)
      const estimatedLimit = 5 * 1024 * 1024 // 5MB
      const available = Math.max(0, estimatedLimit - used)
      const percentage = (used / estimatedLimit) * 100

      return { used, available, percentage }
    } catch (error) {
      console.error('Error getting storage info:', error)
      return { used: 0, available: 0, percentage: 0 }
    }
  }

  /**
   * Encrypts data before storage (simple base64 encoding for demo)
   * In production, use proper encryption libraries
   * @param data Data to encrypt
   * @returns Encrypted data
   */
  private encryptData(data: TokenStorage): { token: string; refreshToken: string; userData: string } {
    return {
      token: this.encrypt(data.token),
      refreshToken: this.encrypt(data.refreshToken),
      userData: this.encrypt(
        JSON.stringify({
          userId: data.userId,
          role: data.role,
          refreshTokenExpiresAt: data.refreshTokenExpiresAt,
        })
      ),
    }
  }

  /**
   * Decrypts data after retrieval
   * @param encryptedData Encrypted data
   * @returns Decrypted token storage data
   */
  private decryptData(encryptedData: { token: string; refreshToken: string; userData: string }): TokenStorage {
    const userData = JSON.parse(this.decrypt(encryptedData.userData))

    // Backward compatibility: handle legacy token payloads that mistakenly stored the entire object
    let token = this.decrypt(encryptedData.token)
    if (token && token.startsWith('{')) {
      try {
        const legacy = JSON.parse(token)
        if (legacy && typeof legacy.token === 'string') {
          token = legacy.token
        }
      } catch {
        // ignore legacy parse errors
      }
    }

    return {
      token,
      refreshToken: this.decrypt(encryptedData.refreshToken),
      refreshTokenExpiresAt: userData.refreshTokenExpiresAt,
      userId: userData.userId,
      role: userData.role,
    }
  }

  /**
   * Simple encryption (base64 encoding)
   * @param text Text to encrypt
   * @returns Encrypted text
   */
  private encrypt(text: string): string {
    return btoa(encodeURIComponent(text))
  }

  /**
   * Simple decryption (base64 decoding)
   * @param encryptedText Encrypted text
   * @returns Decrypted text
   */
  private decrypt(encryptedText: string): string {
    return decodeURIComponent(atob(encryptedText))
  }

  /**
   * Clears expiration timestamps from storage
   * @param storage Storage object to clear from
   */
  private clearExpirationTimestamps(storage: Storage): void {
    const keys = Object.keys(storage)
    const expirationKeys = keys.filter(key => key.startsWith(`${this.STORAGE_PREFIX}exp_`))
    expirationKeys.forEach(key => storage.removeItem(key))
  }

  /**
   * Clears all tokens for a specific user
   * @param userId User ID
   * @param storage Storage object to clear from
   */
  private clearUserTokens(userId: string, storage: Storage): void {
    storage.removeItem(this.TOKEN_KEY)
    storage.removeItem(this.REFRESH_TOKEN_KEY)
    storage.removeItem(this.USER_DATA_KEY)
    storage.removeItem(`${this.STORAGE_PREFIX}exp_${userId}`)
  }

  /** Returns true only when BOTH the SSO token AND the SSO user object are present */
  get isSsoTokenExists(): boolean {
    return !!localStorage.getItem(this.SSO_TOKEN_KEY) && !!localStorage.getItem(this.SSO_USER_INFO)
  }

  ssoToken(): string {
    return localStorage.getItem(this.SSO_TOKEN_KEY) ?? ''
  }

  ssoRefreshToken(): string | null {
    return localStorage.getItem(this.SSO_REFRESH_TOKEN_KEY)
  }

  ssoRefreshExpires(): string | null {
    return localStorage.getItem(this.SSO_REFRESH_EXPIRES_KEY)
  }

  ssoUser(): AuthSsoUser {
    return localStorage.getItem(this.SSO_USER_INFO) ? JSON.parse('' + localStorage.getItem(this.SSO_USER_INFO)) : null
  }
}
